import React, { useState, useEffect, useRef } from 'react';
import AdminEditLeadModal from './components/admin/AdminEditLeadModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { useGamification } from './hooks/useGamification';
import { buildHeaders } from './services/apiService';

const LeaderboardPage = ({
  onBack,
  leaderboardData,
  backendUrl,
  onViewUserSubmissions,
  getSubmittedByName,
  currentUser,
  isSubmitting, // ✅ NEW: Shows if submission is in progress
  currentSubmission, // ✅ NEW: The submission result
  selectedImage, // ✅ NEW: The image being submitted
  businessName // ✅ NEW: Business name being submitted
}) => {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const { isLeaderboardEnabled, loading: flagsLoading } = useFeatureFlags();
  const { getUserTotalPoints } = useGamification();
  
  // Redirect if leaderboard is disabled
  useEffect(() => {
    if (!flagsLoading && !isLeaderboardEnabled) {
      console.log('🚫 Leaderboard disabled, redirecting to home');
      navigate('/', { replace: true });
    }
  }, [flagsLoading, isLeaderboardEnabled, navigate]);
  
  // Pagination state for top users
  const [currentPage, setCurrentPage] = useState(0);
  const usersPerPage = 10;

  // Recent scans state
  const [recentScans, setRecentScans] = useState([]);
  const [recentScansPage, setRecentScansPage] = useState(0);
  const [showRecentDropdown, setShowRecentDropdown] = useState(false);
  const recentScansPerPage = 5;

  // Auto-rotating carousel state
  const [currentRotatingIndex, setCurrentRotatingIndex] = useState(0);

  // My Latest Scan state
  const [myLatestScan, setMyLatestScan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmojiBurst, setShowEmojiBurst] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deepLinkInspection, setDeepLinkInspection] = useState(null);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);

  // Leaderboard time period state
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all_time');
  const [periodLeaderboardData, setPeriodLeaderboardData] = useState(null);

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');

  // Fetch available campaigns on mount
  useEffect(() => {
    if (backendUrl === undefined) return;
    const fetchCampaigns = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/campaigns?include_completed=true`, {
          headers: buildHeaders(),
        });
        const data = await res.json();
        if (data.success) setCampaigns(data.campaigns || []);
      } catch (err) {
        console.error('Failed to load campaigns:', err);
      }
    };
    fetchCampaigns();
  }, [backendUrl]);

  // Reset pagination when period or campaign changes
  useEffect(() => {
    setCurrentPage(0);
    setPeriodLeaderboardData(null);
  }, [leaderboardPeriod, selectedCampaignId]);

  // Fetch period-based leaderboard data (campaign-aware)
  useEffect(() => {
    const fetchPeriodLeaderboard = async () => {
      if (backendUrl === undefined) return;
      if (!localStorage.getItem('token')) return;
      try {
        let url = `${backendUrl}/api/leaderboard/stats?period=${leaderboardPeriod}`;
        if (selectedCampaignId) url += `&campaign_id=${selectedCampaignId}`;

        const response = await fetch(url, { headers: buildHeaders() });
        const data = await response.json();

        if (data.success) {
          setPeriodLeaderboardData(data);
        }
      } catch (error) {
        console.error('Error fetching period leaderboard:', error);
      }
    };

    fetchPeriodLeaderboard();
  }, [backendUrl, leaderboardPeriod, selectedCampaignId]);

  // 🎉 Check for pending celebration on mount (user navigated here after submission completed elsewhere)
  useEffect(() => {
    const pendingCelebration = sessionStorage.getItem('pendingCelebration');
    if (pendingCelebration) {
      console.log('🎉 CELEBRATION-CHECK: Found pending celebration!');
      // 🔧 FIX: If we have a pending celebration, we are essentially "processing" the UI transition
      // This prevents "No scans yet" from flashing before data loads
      setIsProcessing(true);

      const celebrationData = JSON.parse(pendingCelebration);

      // Only trigger if it's recent (within last 60 seconds)
      const isRecent = Date.now() - celebrationData.timestamp < 60000;
      if (isRecent) {
        console.log('🎉 CELEBRATION-CHECK: Triggering delayed celebration!');
        sessionStorage.removeItem('pendingCelebration');

        // Small delay for visual effect
        setTimeout(() => {
          setShowEmojiBurst(true);
          setTimeout(() => setShowEmojiBurst(false), 5000);
        }, 500);
      } else {
        console.log('🎉 CELEBRATION-CHECK: Celebration expired (>60s old), clearing');
        sessionStorage.removeItem('pendingCelebration');
        // If expired, stop "processing" state immediately
        setIsProcessing(false);
      }
    }
  }, []);

  // Listen for submission events (cross-page real-time communication)
  useEffect(() => {
    console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: Setting up event listeners...', {
      backendUrl,
      currentUser
    });

    const handlePhotoCaptured = (e) => {
      console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: 📸 PHOTO CAPTURED EVENT RECEIVED!');
      // INSTANTLY show the captured photo (even before business name selected)
      const newScan = {
        image_url: e.detail.image,
        thumbnail_url: e.detail.image,
        business_name: 'Capturing location...',
        submitted_by: currentUser || 'You',
        created_at: e.detail.timestamp,
        status: 'capturing'
      };
      setMyLatestScan(newScan);
      setIsProcessing(true);
    };

    const handleSubmissionStarted = (e) => {
      console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: 🎯 SUBMISSION STARTED EVENT RECEIVED!');
      // Update with business name when submission actually starts
      setMyLatestScan(prev => {
        const updated = {
          ...prev,
          business_name: e.detail.businessName,
          submitted_by: e.detail.submittedBy,
          status: 'processing'
        };
        return updated;
      });
      setIsProcessing(true);
    };

    const handleSubmissionCompleted = (e) => {
      console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: 🎉 SUBMISSION COMPLETED EVENT RECEIVED!');
      setIsProcessing(false);

      // Clear the sessionStorage flag since we're handling it directly
      sessionStorage.removeItem('pendingCelebration');

      // 🎉 TRIGGER EMOJI BURST! - Enhanced with better timing and logging
      console.log('🎉 EMOJI-BURST: Triggering emoji celebration!');
      setShowEmojiBurst(true);
      // Keep emojis visible longer (5 seconds instead of 3)
      setTimeout(() => {
        console.log('🎉 EMOJI-BURST: Hiding emojis after 5 seconds');
        setShowEmojiBurst(false);
      }, 5000);

      // Fetch fresh data from database
      setTimeout(() => {
        if (currentUser && backendUrl) {
          const userName = encodeURIComponent(currentUser);
          console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: Fetching fresh data');
          fetch(`${backendUrl}/api/inspections/user/${userName}?limit=1`, { headers: buildHeaders() })
            .then(r => r.json())
            .then(data => {
              console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: Fresh data received');
              if (data && data.length > 0) {
                setMyLatestScan(data[0]);
              }
            })
            .catch(err => {
              console.error('🔍 REALTIME-DEBUG [LeaderboardPage]: Error fetching fresh data:', err);
            });
        }
      }, 1000);
    };

    const handleSubmissionFailed = (e) => {
      console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: ❌ SUBMISSION FAILED EVENT RECEIVED!');
      setIsProcessing(false);
    };

    window.addEventListener('photo-captured', handlePhotoCaptured);
    window.addEventListener('submission-started', handleSubmissionStarted);
    window.addEventListener('submission-completed', handleSubmissionCompleted);
    window.addEventListener('submission-failed', handleSubmissionFailed);

    console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: ✅ Event listeners attached!');

    return () => {
      console.log('🔍 REALTIME-DEBUG [LeaderboardPage]: Removing event listeners...');
      window.removeEventListener('photo-captured', handlePhotoCaptured);
      window.removeEventListener('submission-started', handleSubmissionStarted);
      window.removeEventListener('submission-completed', handleSubmissionCompleted);
      window.removeEventListener('submission-failed', handleSubmissionFailed);
    };
  }, [backendUrl, currentUser]);

  // 🔧 FIX: Sync isProcessing local state with isSubmitting prop
  useEffect(() => {
    if (isSubmitting) {
      console.log('🔍 LEADERBOARD-STATE: isSubmitting prop is TRUE, setting isProcessing=true');
      setIsProcessing(true);
    }
  }, [isSubmitting]);

  // 🔧 FIX: Use ref to prevent race conditions with interval creation
  const latestScanIntervalRef = useRef(null);
  const latestScanFetchInProgressRef = useRef(false);

  // Fetch MY latest scan from database (always on mount, and poll when not submitting)
  useEffect(() => {
    const fetchMyLatestScan = async () => {
      if (!currentUser || currentUser === 'Not logged in') return;
      
      // 🔧 CRITICAL: Prevent multiple simultaneous fetches
      if (latestScanFetchInProgressRef.current) {
        console.log('⏭️ LEADERBOARD-FETCH: Skipping (fetch already in progress)');
        return;
      }

      latestScanFetchInProgressRef.current = true;
      console.log('🔍 LEADERBOARD-FETCH: Fetching latest scan');

      try {
        const userName = encodeURIComponent(currentUser);
        const response = await fetch(`${backendUrl}/api/inspections/user/${userName}?limit=1`, { headers: buildHeaders() });
        const data = await response.json();

        if (data && data.length > 0) {
          setMyLatestScan(data[0]);
          // If we got data and were processing, we're done processing
          if (isProcessing && !isSubmitting) {
            console.log('🔍 LEADERBOARD-FETCH: Got data, clearing isProcessing');
            setIsProcessing(false);
          }
        }
      } catch (error) {
        console.error('Error fetching my latest scan:', error);
      } finally {
        latestScanFetchInProgressRef.current = false;
      }
    };

    // 🔧 CRITICAL FIX: Clear any existing interval before creating a new one
    if (latestScanIntervalRef.current) {
      console.log('🧹 LEADERBOARD-FETCH: Clearing previous interval');
      clearInterval(latestScanIntervalRef.current);
      latestScanIntervalRef.current = null;
    }

    if (backendUrl !== undefined && currentUser) {
      // ✅ Fetch once on mount (no polling!)
      // Events will handle real-time updates (submission-completed, etc.)
      fetchMyLatestScan();
      console.log('✅ LEADERBOARD-FETCH: Initial fetch complete (event-driven, no polling)');
    } else {
      console.log('⚠️ LEADERBOARD-FETCH: Not fetching because:', { backendUrlUndefined: backendUrl === undefined, noCurrentUser: !currentUser });
    }
  }, [backendUrl, currentUser]); // ✅ Event-driven: No polling intervals!

  // 🔧 FIX: Use ref to prevent race conditions with interval creation
  const recentScansIntervalRef = useRef(null);
  const recentScansFetchInProgressRef = useRef(false);

  // Fetch recent scans
  useEffect(() => {
    const fetchRecentScans = async () => {
      // 🔧 CRITICAL: Prevent multiple simultaneous fetches
      if (recentScansFetchInProgressRef.current) {
        console.log('⏭️ RECENT-SCANS-FETCH: Skipping (fetch already in progress)');
        return;
      }

      recentScansFetchInProgressRef.current = true;
      console.log('🔍 RECENT-SCANS-FETCH: Starting...', { backendUrl });

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${backendUrl}/api/admin/inspections?status=all&limit=20&sort=created_at&order=desc`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const data = await response.json();

        if (data.success) {
          setRecentScans(data.inspections || []);
        } else {
          console.log('⚠️ RECENT-SCANS-FETCH: data.success is false');
        }
      } catch (error) {
        console.error('❌ Error fetching recent scans:', error);
      } finally {
        recentScansFetchInProgressRef.current = false;
      }
    };

    // 🔧 CRITICAL FIX: Clear any existing interval before creating a new one
    if (recentScansIntervalRef.current) {
      console.log('🧹 RECENT-SCANS-FETCH: Clearing previous interval');
      clearInterval(recentScansIntervalRef.current);
      recentScansIntervalRef.current = null;
    }

    if (backendUrl !== undefined) {
      // ✅ Fetch once on mount (no polling!)
      // User can manually refresh if they want to see latest submissions
      fetchRecentScans();
      console.log('✅ RECENT-SCANS-FETCH: Initial fetch complete (no polling, manual refresh only)');
    } else {
      console.log('⚠️ RECENT-SCANS-FETCH: backendUrl is undefined, skipping');
    }
  }, [backendUrl]);

  // Deep link support: /leaderboard?openInspectionId=<uuid>
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search || '');
    const openInspectionId = params.get('openInspectionId');
    if (!openInspectionId) return;
    if (!backendUrl) return;
    if (!currentUser || currentUser === 'Not logged in') return;

    const fetchInspection = async () => {
      try {
        setDeepLinkLoading(true);
        // Base64 encode email to bypass WAF
        const encodedEmail = btoa(currentUser);
        const url = `${backendUrl}/api/inspections/${encodeURIComponent(openInspectionId)}/user-view?user_email=${encodedEmail}`;
        const res = await fetch(url, { headers: buildHeaders() });
        const data = await res.json();
        if (res.ok && data && data.success && data.inspection) {
          setDeepLinkInspection(data.inspection);
          setShowEditModal(true);
        } else {
          console.warn('⚠️ Deep link inspection fetch failed:', data);
        }
      } catch (e) {
        console.error('❌ Deep link inspection fetch error:', e);
      } finally {
        setDeepLinkLoading(false);
      }
    };

    fetchInspection();
  }, [routerLocation.search, backendUrl, currentUser]);

  // Auto-rotate through the first 5 recent scans every 10 seconds
  useEffect(() => {
    if (recentScans.length === 0) return;

    const rotateInterval = setInterval(() => {
      setCurrentRotatingIndex(prev => (prev + 1) % Math.min(5, recentScans.length));
    }, 10000); // Rotate every 10 seconds

    return () => clearInterval(rotateInterval);
  }, [recentScans]);

  // Show ALL users (no artificial limit - was previously limited to 10)
  // Use period data if available, otherwise fallback to prop data
  const topUsers = (periodLeaderboardData?.leaderboard || leaderboardData || []);
  const myPoints = getUserTotalPoints();

  // Pagination for top users
  const totalPages = Math.min(Math.ceil(topUsers.length / usersPerPage), 10); // Max 10 pages (top 100)

  // 🔧 FIX: Safety check - if currentPage is beyond available pages, reset to last valid page
  const safePage = Math.min(currentPage, Math.max(0, totalPages - 1));
  if (safePage !== currentPage && totalPages > 0) {
    console.log(`⚠️ PAGINATION-FIX: currentPage ${currentPage} is out of bounds (totalPages: ${totalPages}), resetting to ${safePage}`);
    setCurrentPage(safePage);
  }

  const paginatedUsers = topUsers.slice(safePage * usersPerPage, (safePage + 1) * usersPerPage);

  // Pagination for recent scans
  const totalRecentPages = Math.ceil(recentScans.length / recentScansPerPage);
  const paginatedRecentScans = recentScans.slice(recentScansPage * recentScansPerPage, (recentScansPage + 1) * recentScansPerPage);

  // Check if user can view submissions (only their own)
  // Check if user can view submissions (only their own)
  const canViewSubmissions = (userName) => {
    if (!currentUser || currentUser === 'Not logged in') return false;
    if (!userName) return false; // Safety check/fix for null names

    // Match either full email or just the name part
    const currentUserName = currentUser.includes('@') ? currentUser.split('@')[0] : currentUser;
    const targetUserName = userName.includes('@') ? userName.split('@')[0] : userName;
    return currentUserName.toLowerCase() === targetUserName.toLowerCase();
  };

  return (
    <div className="min-h-screen bg-white p-4 md:p-8">
      {/* Smooth animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes borderPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(13, 177, 75, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(13, 177, 75, 0.1); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes floatUp {
          0% { 
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: translateY(-150px) rotate(180deg) scale(1.2);
            opacity: 0.8;
          }
          100% { 
            transform: translateY(-300px) rotate(360deg) scale(0.5);
            opacity: 0;
          }
        }
      `}</style>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <span className="text-6xl">🏆</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[var(--pbfs-navy)] mb-2">
            Leaderboard
          </h1>
          <p className="text-gray-600 text-lg">
            TagQuest Champions
          </p>
        </div>

        {/* 🆕 MY LATEST SCAN - Always show for logged-in users */}
        {currentUser && currentUser !== 'Not logged in' && (
          <div className="bg-gradient-to-br from-[var(--pbfs-navy)] to-[var(--pbfs-blue)] border-2 border-[var(--pbfs-green)] rounded-xl p-6 mb-8 relative overflow-hidden">
            {/* Emoji Celebration Burst - triggers when submission completes */}
            {showEmojiBurst && (
              <div className="absolute inset-0 pointer-events-none z-20 overflow-visible">
                {/* Multiple waves of emojis for more impact */}
                {['🎉', '🎊', '⭐', '🏆', '💥', '✨', '🔥', '💫', '🚀', '💯'].map((emoji, idx) => (
                  <div
                    key={`wave1-${idx}`}
                    className="absolute text-5xl drop-shadow-lg"
                    style={{
                      left: `${5 + idx * 9}%`,
                      animation: `floatUp ${2.5 + Math.random()}s ease-out forwards`,
                      animationDelay: `${idx * 0.1}s`,
                      bottom: '-20px',
                      filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.5))'
                    }}
                  >
                    {emoji}
                  </div>
                ))}
                {/* Second wave with offset */}
                {['✨', '🌟', '💫', '⚡', '🔥'].map((emoji, idx) => (
                  <div
                    key={`wave2-${idx}`}
                    className="absolute text-4xl"
                    style={{
                      left: `${10 + idx * 18}%`,
                      animation: `floatUp ${3 + Math.random()}s ease-out forwards`,
                      animationDelay: `${0.5 + idx * 0.15}s`,
                      bottom: '-20px'
                    }}
                  >
                    {emoji}
                  </div>
                ))}
                {/* Center burst text */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 text-4xl font-bold text-[var(--pbfs-green)] drop-shadow-lg"
                  style={{
                    bottom: '50%',
                    animation: 'fadeIn 0.5s ease-out forwards',
                    textShadow: '0 0 20px rgba(13, 177, 75, 0.8)'
                  }}
                >
                  🎯 SUCCESS!
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span>⚡</span> My Latest Scan
            </h2>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-white/20">
              <div className="flex gap-2 sm:gap-4 items-center">
                {/* Thumbnail - Click to expand */}
                <div
                  className="flex-shrink-0 cursor-pointer group relative"
                  onClick={() => {
                    // Use current submission image if processing (prop OR local state), otherwise use saved scan
                    const imageToShow = (isSubmitting || isProcessing) ? (selectedImage || myLatestScan?.image_url) : (myLatestScan?.image_url);
                    if (imageToShow) setExpandedImage(imageToShow);
                  }}
                >
                  {/* 🔧 FIX: Check both isSubmitting prop AND local isProcessing state */}
                  {(isSubmitting || isProcessing || myLatestScan) ? (
                    <img
                      src={(isSubmitting || isProcessing) ? (selectedImage || myLatestScan?.thumbnail_url || myLatestScan?.image_url) : (myLatestScan?.thumbnail_url || myLatestScan?.image_url)}
                      alt="Latest scan"
                      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover border-2 ${(isSubmitting || isProcessing) ? 'border-yellow-400 animate-pulse' : 'border-[var(--pbfs-green)]'} group-hover:scale-110 transition-transform shadow-lg`}
                    />
                  ) : (
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-white/10 border-2 border-dashed border-[var(--pbfs-green)]/30 flex items-center justify-center">
                      <span className="text-2xl sm:text-3xl">📸</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-all flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-xl sm:text-2xl">🔍</span>
                  </div>
                </div>

                {/* Scan Details - Flexible middle section */}
                <div className="flex-1 min-w-0">
                  <div
                    className="text-white font-bold text-sm sm:text-lg cursor-pointer hover:text-[var(--pbfs-green)] transition-colors truncate"
                    onClick={() => onViewUserSubmissions && onViewUserSubmissions(currentUser)}
                    title="View all your submissions"
                  >
                    {/* 🔧 FIX: Check both isSubmitting prop AND local isProcessing state */}
                    {(isSubmitting || isProcessing)
                      ? (businessName || myLatestScan?.business_name || '⏳ Processing...')
                      : (myLatestScan?.business_name || myLatestScan?.site_street_address || 'No scans yet - capture your first tag!')}
                  </div>
                  <div className="text-white/70 text-xs sm:text-sm mt-0.5 truncate">
                    {(isSubmitting || isProcessing)
                      ? '🤖 Analyzing with AI...'
                      : myLatestScan && (
                        <>
                          {myLatestScan.site_street_address && `${myLatestScan.site_street_address}, `}
                          {myLatestScan.site_city}, {myLatestScan.site_state}
                        </>
                      )}
                  </div>
                  <div className="text-white/50 text-xs mt-1 hidden sm:block">
                    {(isSubmitting || isProcessing)
                      ? 'Just now'
                      : myLatestScan && new Date(myLatestScan.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZone: 'America/Chicago'
                      })}
                  </div>

                  {/* Processing Indicator or Status - 🔧 FIX: Use both prop AND local state */}
                  <div className="mt-2 sm:mt-3 flex items-center gap-2">
                    {(isSubmitting || isProcessing) ? (
                      <div className="flex items-center gap-1.5 text-yellow-400 animate-pulse">
                        <div className="animate-spin text-sm sm:text-xl">⚙️</div>
                        <span className="text-xs sm:text-sm font-semibold">Processing...</span>
                      </div>
                    ) : myLatestScan ? (
                      <>
                        <div className="flex items-center gap-1.5 text-[var(--pbfs-green)]">
                          <span className="text-sm sm:text-xl">✅</span>
                          <span className="text-xs sm:text-sm font-semibold">Complete</span>
                        </div>
                        {/* Edit Button - Compact on mobile */}
                        <button
                          onClick={() => setShowEditModal(true)}
                          className="ml-auto text-xs bg-white/20 hover:bg-white/30 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-white/80 hover:text-white transition-all flex items-center gap-1"
                          title="Edit this submission"
                        >
                          ✏️ <span className="hidden sm:inline">Edit</span>
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>

                {/* Points Display - Compact on mobile */}
                <div className="flex-shrink-0 text-center">
                  <div className="text-[var(--pbfs-green)] text-xl sm:text-3xl font-bold">
                    {myPoints}
                  </div>
                  <div className="text-white/60 text-[10px] sm:text-xs">pts</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🆕 Recent Scans Section */}
        {recentScans.length > 0 && (
          <div className="bg-[var(--pbfs-green)]/10 border-2 border-[var(--pbfs-green)]/30 rounded-xl p-6 mb-8">
            <h2 className="text-2xl font-bold text-[var(--pbfs-navy)] mb-4 flex items-center gap-2">
              <span>⚡</span> Most Recent Scans
            </h2>

            {/* Auto-rotating most recent scan (cycles through top 5 every 10 seconds) */}
            {recentScans[currentRotatingIndex] && (
              <div
                className="bg-white rounded-lg p-4 mb-3 border-2 border-[var(--pbfs-green)] cursor-pointer hover:shadow-lg transition-all transform hover:scale-[1.02] animate-fadeIn relative overflow-hidden"
                onClick={() => setShowRecentDropdown(!showRecentDropdown)}
                title="Click to see all recent scans"
                style={{
                  animation: 'fadeIn 0.5s ease-in-out, borderPulse 2s ease-in-out infinite'
                }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="font-bold text-[var(--pbfs-navy)] text-lg">
                      {recentScans[currentRotatingIndex].submitted_by || 'Anonymous'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {recentScans[currentRotatingIndex].business_name ||
                        recentScans[currentRotatingIndex].site_street_address ||
                        'Recent scan'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {recentScans[currentRotatingIndex].site_city && recentScans[currentRotatingIndex].site_state
                        ? `${recentScans[currentRotatingIndex].site_city}, ${recentScans[currentRotatingIndex].site_state}`
                        : 'Location'}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-xs text-gray-500">
                      {new Date(recentScans[currentRotatingIndex].created_at).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZone: 'America/Chicago'
                      })}
                    </div>
                    <div className="text-[10px] text-[var(--pbfs-green)] font-semibold">
                      {currentRotatingIndex + 1} of {Math.min(5, recentScans.length)}
                    </div>
                  </div>
                </div>
                {/* Rotation indicator dots */}
                <div className="flex justify-center gap-1 mt-3">
                  {recentScans.slice(0, 5).map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-all ${idx === currentRotatingIndex
                          ? 'bg-[var(--pbfs-green)] w-6'
                          : 'bg-gray-300'
                        }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Dropdown content (no button needed - clicking card opens it) */}
            {showRecentDropdown && (
              <div className="mt-3 space-y-2">
                {paginatedRecentScans.map((scan, idx) => {
                  if (idx === currentRotatingIndex) return null; // Skip the one currently showing
                  return (
                    <div key={scan.id || idx} className="bg-white/70 rounded-lg p-3 hover:bg-white transition-colors">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="font-semibold text-[var(--pbfs-navy)] text-sm">
                            {scan.submitted_by || 'Anonymous'}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {scan.business_name || scan.site_street_address || 'Recent scan'}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {scan.site_city && scan.site_state ? `${scan.site_city}, ${scan.site_state}` : ''}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(scan.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            timeZone: 'America/Chicago'
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination for recent scans */}
                {totalRecentPages > 1 && (
                  <div className="flex justify-center gap-2 mt-3">
                    <button
                      onClick={() => setRecentScansPage(Math.max(0, recentScansPage - 1))}
                      disabled={recentScansPage === 0}
                      className="px-3 py-1 bg-white rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    >
                      ← Prev
                    </button>
                    <span className="px-3 py-1 text-sm text-[var(--pbfs-navy)]">
                      Page {recentScansPage + 1} of {totalRecentPages}
                    </span>
                    <button
                      onClick={() => setRecentScansPage(Math.min(totalRecentPages - 1, recentScansPage + 1))}
                      disabled={recentScansPage >= totalRecentPages - 1}
                      className="px-3 py-1 bg-white rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Campaign Selector + Leaderboard Tabs */}
        <div className="mb-6">
          {/* Campaign dropdown (only shows when campaigns exist) */}
          {campaigns.length > 0 && (
            <div className="flex justify-center mb-4">
              <select
                value={selectedCampaignId}
                onChange={(e) => setSelectedCampaignId(e.target.value)}
                className="px-4 py-2.5 rounded-lg border-2 border-[var(--pbfs-navy)]/20 bg-white text-[var(--pbfs-navy)] font-semibold text-sm focus:border-[var(--pbfs-green)] focus:outline-none transition-colors min-w-[200px]"
              >
                <option value="">Overall (All-Time)</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.status === 'completed' ? ' (ended)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-center gap-2 mb-4">
            <button
              onClick={() => setLeaderboardPeriod('this_week')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${leaderboardPeriod === 'this_week'
                  ? 'bg-[var(--pbfs-green)] text-white shadow-lg'
                  : 'bg-white/80 text-[var(--pbfs-navy)] hover:bg-white'
                }`}
            >
              This Week
            </button>
            <button
              onClick={() => setLeaderboardPeriod('this_month')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${leaderboardPeriod === 'this_month'
                  ? 'bg-[var(--pbfs-green)] text-white shadow-lg'
                  : 'bg-white/80 text-[var(--pbfs-navy)] hover:bg-white'
                }`}
            >
              This Month
            </button>
            <button
              onClick={() => setLeaderboardPeriod('all_time')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${leaderboardPeriod === 'all_time'
                  ? 'bg-[var(--pbfs-green)] text-white shadow-lg'
                  : 'bg-white/80 text-[var(--pbfs-navy)] hover:bg-white'
                }`}
            >
              {selectedCampaignId ? 'Full Campaign' : 'Hall of Flame'}
            </button>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-bold text-[var(--pbfs-navy)]">
              {periodLeaderboardData?.campaign
                ? periodLeaderboardData.campaign.name
                : periodLeaderboardData?.period_label || 'Hall of Flame'}
            </h2>
            <p className="text-gray-600 text-sm">
              {periodLeaderboardData?.campaign && (
                <>{periodLeaderboardData.campaign.start_date} to {periodLeaderboardData.campaign.end_date} &bull; </>
              )}
              {!selectedCampaignId && leaderboardPeriod === 'all_time' && 'All-Time Rankings \u2022 '}
              {periodLeaderboardData?.start_date && leaderboardPeriod !== 'all_time' && `Since ${new Date(periodLeaderboardData.start_date).toLocaleDateString()} \u2022 `}
              {(periodLeaderboardData?.leaderboard || leaderboardData || []).length} users
            </p>
          </div>
        </div>

        {/* Leaderboard Cards */}
        <div className="space-y-4 mb-8">
          {paginatedUsers.map((user, index) => {
            // Adjust index for pagination - use safePage to ensure correctness
            const globalIndex = safePage * usersPerPage + index;
            // Medal color for top 3 (based on global index)
            const medalColor =
              globalIndex === 0 ? 'text-[var(--pbfs-green)]' :
                globalIndex === 1 ? 'text-[var(--pbfs-blue)]' :
                  globalIndex === 2 ? 'text-[var(--pbfs-gray)]' :
                    'text-[var(--pbfs-gray)]';

            const medal = globalIndex === 0 ? '👑' : globalIndex === 1 ? '🥈' : globalIndex === 2 ? '🥉' : `#${globalIndex + 1}`;

            return (
              <div
                key={user.name || index}
                className="bg-gray-100 backdrop-blur-md rounded-xl p-4 sm:p-5 border-2 border-gray-300 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <div className="flex flex-row items-center justify-between gap-3 sm:gap-4">
                  {/* Left: Rank & User Info */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Medal/Rank - Compact */}
                    <div className={`text-3xl sm:text-4xl ${medalColor} flex-shrink-0`}>
                      {medal}
                    </div>

                    {/* User Info - Clickable only if it's the logged-in user */}
                    <div className="min-w-0 flex-1">
                      {canViewSubmissions(user.name) ? (
                        <button
                          onClick={() => {
                            if (onViewUserSubmissions) {
                              onViewUserSubmissions(user.name);
                            }
                          }}
                          className="text-base sm:text-xl font-bold text-[var(--pbfs-navy)] hover:text-[var(--pbfs-green)] hover:underline transition-colors text-left cursor-pointer truncate w-full block"
                          title="View your submissions"
                        >
                          {user.name || 'Anonymous User'}
                        </button>
                      ) : (
                        <div className="text-base sm:text-xl font-bold text-[var(--pbfs-navy)] cursor-default truncate">
                          {user.name || 'Anonymous User'}
                        </div>
                      )}
                      <p className="text-gray-600 text-xs sm:text-sm truncate">
                        {user.title ||
                          (globalIndex === 0 ? 'Legend of Life Safety' :
                            globalIndex === 1 ? 'Legend of Life Safety' :
                              globalIndex === 2 ? 'Marshal of Defense' :
                                'Fire Safety Hero')}
                      </p>
                    </div>
                  </div>

                  {/* Right: Points - Compact, always on right */}
                  <div className="flex flex-col items-center justify-center flex-shrink-0">
                    <div className="text-2xl sm:text-3xl font-bold text-pb-green">
                      {user.points || 0}
                    </div>
                    <div className="text-gray-600 text-[10px] sm:text-xs">
                      points
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mb-8">
            <button
              onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="px-6 py-3 bg-white border-2 border-[var(--pbfs-navy)] text-[var(--pbfs-navy)] font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              ← Previous
            </button>
            <div className="text-[var(--pbfs-navy)] font-semibold">
              Page {safePage + 1} of {totalPages}
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage >= totalPages - 1}
              className="px-6 py-3 bg-white border-2 border-[var(--pbfs-navy)] text-[var(--pbfs-navy)] font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next →
            </button>
          </div>
        )}

        {/* Back Button */}
        <div className="text-center">
          <button
            onClick={onBack}
            className="bg-gray-200 hover:bg-gray-300 text-[var(--pbfs-navy)] font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl text-lg flex items-center justify-center space-x-2 mx-auto"
          >
            <span>←</span>
            <span>Back to TagQuest</span>
          </button>

          <p className="text-gray-600 text-sm mt-4">
            Keep scanning to climb the leaderboard!
          </p>
        </div>
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={expandedImage}
              alt="Expanded view"
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            />
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold shadow-lg"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Edit Submission Modal - Using AdminEditLeadModal for reliability */}
      <AdminEditLeadModal
        isOpen={showEditModal}
        inspection={deepLinkInspection || myLatestScan}
        backendUrl={backendUrl}
        onClose={() => {
          setShowEditModal(false);
          setDeepLinkInspection(null);
        }}
        onSaved={(updated) => {
          if (deepLinkInspection) {
            setDeepLinkInspection(updated);
          } else {
            setMyLatestScan(updated);
          }
        }}
      />
    </div>
  );
};

export default LeaderboardPage;
